import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FieldHint } from '@/components/shared/FieldHint';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { createPrincipal } from '@/api/principals';
import { queryKeys } from '@/api/queryKeys';

interface AddPrincipalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Add a user to the org's built-in local directory.
 *
 * No identity provider is needed: this is how a Free or Team org gets its
 * first principal and therefore its first retrieval. Groups drive RBAC
 * conditions (`principal.groups contains ...`), so they are first-class here.
 */
export function AddPrincipalDialog({ open, onOpenChange }: AddPrincipalDialogProps) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [groups, setGroups] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setEmail('');
    setDisplayName('');
    setGroups('');
    setError(null);
  };

  const mutation = useMutation({
    mutationFn: () =>
      createPrincipal({
        email: email.trim(),
        display_name: displayName.trim() || undefined,
        groups: groups
          .split(',')
          .map((g) => g.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.principals.all });
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      reset();
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : 'Could not add the user. Try again.';
      setError(message);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription>
            Adds a principal to your local directory. No identity provider needed. Policies
            match on the groups you set here.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            mutation.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="principal-email">Email</Label>
            <Input
              id="principal-email"
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alice@yourco.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="principal-name">Display name</Label>
            <Input
              id="principal-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Alice Chen"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="principal-groups">Groups</Label>
            <Input
              id="principal-groups"
              value={groups}
              onChange={(e) => setGroups(e.target.value)}
              placeholder="engineering, finance"
            />
            <FieldHint>
              Comma-separated. Case-sensitive. Policies match these with principal.groups
              contains engineering, so use the same spelling here that you use in the policy.
            </FieldHint>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending || !email.trim()}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add user
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
